import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, CreditCard, Calendar, Clock, Play, Pause, X, 
  RefreshCcw, AlertCircle, CheckCircle, Loader2, Package,
  ArrowUpCircle, ArrowDownCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { handleImageError } from '@/utils/imageUtils';
import ResilientImg from '@/components/ResilientImg';

function MySubscriptions() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [cancelReason, setCancelReason] = useState('');

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (!storedUser) {
      navigate('/');
      return;
    }
    setUser(JSON.parse(storedUser));
    loadSubscriptions();
  }, []);

  const loadSubscriptions = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/subscriptions/my-subscriptions');
      setSubscriptions(response.data.subscriptions || []);
    } catch (error) {
      console.error('Error loading subscriptions:', error);
      toast.error('Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (subscriptionId, action, reason = null) => {
    try {
      setActionLoading(subscriptionId);
      const payload = { action };
      if (reason) {
        payload.reason = reason;
      }
      
      const response = await axios.post(`/subscriptions/${subscriptionId}/action`, payload);
      toast.success(response.data.message);
      await loadSubscriptions();
      setShowCancelModal(false);
      setSelectedSubscription(null);
      setCancelReason('');
    } catch (error) {
      console.error('Error performing action:', error);
      toast.error(error.response?.data?.detail || 'Failed to perform action');
    } finally {
      setActionLoading(null);
    }
  };

  const handleUpgrade = async (subscriptionId, newInterval) => {
    try {
      setActionLoading(subscriptionId);
      const response = await axios.post(`/subscriptions/${subscriptionId}/upgrade`, {
        new_interval: newInterval
      });
      toast.success(response.data.message);
      await loadSubscriptions();
      setShowUpgradeModal(false);
      setSelectedSubscription(null);
    } catch (error) {
      console.error('Error upgrading subscription:', error);
      toast.error(error.response?.data?.detail || 'Failed to change subscription');
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'text-green-400 bg-green-400/10 border-green-400/30';
      case 'trialing':
        return 'text-blue-400 bg-blue-400/10 border-blue-400/30';
      case 'past_due':
        return 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30';
      case 'paused':
        return 'text-orange-400 bg-orange-400/10 border-orange-400/30';
      case 'canceled':
        return 'text-red-400 bg-red-400/10 border-red-400/30';
      default:
        return 'text-neutral-400 bg-neutral-400/10 border-neutral-400/30';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#00F0FF]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <div className="bg-gradient-to-b from-neutral-900 to-[#050505] border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/profile')}
            className="mb-6 text-neutral-400 hover:text-white"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Profile
          </Button>

          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-gradient-to-br from-[#00F0FF]/20 to-white/20 rounded-2xl flex items-center justify-center">
              <CreditCard className="w-8 h-8 text-[#00F0FF]" />
            </div>
            <div>
              <h1 className="text-4xl font-black" style={{ fontFamily: 'Outfit, sans-serif' }}>
                My Subscriptions
              </h1>
              <p className="text-neutral-400 mt-1">
                Manage your recurring subscriptions
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-12">
        {subscriptions.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-24 h-24 bg-neutral-800/50 rounded-full flex items-center justify-center mx-auto mb-6">
              <Package className="w-12 h-12 text-neutral-600" />
            </div>
            <h2 className="text-2xl font-bold mb-2">No Active Subscriptions</h2>
            <p className="text-neutral-400 mb-6">
              You don&apos;t have any active subscriptions yet.
            </p>
            <Button
              onClick={() => navigate('/')}
              className="bg-[#00F0FF] hover:bg-[#00C2CC] text-black rounded-full font-bold"
            >
              Browse Products
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {subscriptions.map((sub) => (
              <div
                key={sub.subscription_id}
                className="bg-neutral-900/50 border border-white/10 rounded-2xl overflow-hidden"
              >
                <div className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    {/* Product Info */}
                    <div className="flex items-start gap-4">
                      {sub.product?.images?.[0] ? (
                        <ResilientImg
                          src={sub.product.images[0].startsWith('http') ? sub.product.images[0] : `${process.env.REACT_APP_BACKEND_URL}${sub.product.images[0]}`}
                          alt={sub.product?.name}
                          className="w-20 h-20 object-cover rounded-xl"
                        />
                      ) : (
                        <div className="w-20 h-20 bg-neutral-800 rounded-xl flex items-center justify-center">
                          <Package className="w-8 h-8 text-neutral-600" />
                        </div>
                      )}
                      <div>
                        <h3 className="text-xl font-bold">{sub.product?.name || 'Product'}</h3>
                        <p className="text-neutral-400 text-sm mt-1">
                          {sub.interval === 'yearly' ? 'Annual' : 'Monthly'} subscription
                        </p>
                        <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold mt-2 border ${getStatusColor(sub.status)}`}>
                          {sub.status === 'active' && <CheckCircle className="w-3 h-3" />}
                          {sub.status === 'paused' && <Pause className="w-3 h-3" />}
                          {sub.status === 'canceled' && <X className="w-3 h-3" />}
                          {sub.status.charAt(0).toUpperCase() + sub.status.slice(1)}
                        </div>
                      </div>
                    </div>

                    {/* Pricing & Billing Info */}
                    <div className="text-right">
                      <p className="text-3xl font-black text-[#00F0FF]">
                        ${sub.price?.toFixed(2) || '0.00'}
                        <span className="text-sm font-normal text-neutral-400">
                          /{sub.interval === 'yearly' ? 'year' : 'month'}
                        </span>
                      </p>
                      <div className="flex items-center gap-2 justify-end mt-2 text-sm text-neutral-400">
                        <Calendar className="w-4 h-4" />
                        <span>
                          {sub.status === 'canceled' 
                            ? `Ends: ${formatDate(sub.current_period_end)}`
                            : `Renews: ${formatDate(sub.current_period_end)}`
                          }
                        </span>
                      </div>
                      {sub.trial_end && new Date(sub.trial_end) > new Date() && (
                        <div className="flex items-center gap-2 justify-end mt-1 text-sm text-blue-400">
                          <Clock className="w-4 h-4" />
                          <span>Trial until: {formatDate(sub.trial_end)}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  {sub.status !== 'canceled' && (
                    <div className="flex flex-wrap gap-3 mt-6 pt-6 border-t border-white/10">
                      {sub.status === 'active' && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedSubscription(sub);
                              setShowUpgradeModal(true);
                            }}
                            className="border-[#00F0FF]/30 text-[#00F0FF] hover:bg-[#00F0FF]/10"
                          >
                            {sub.interval === 'monthly' ? (
                              <>
                                <ArrowUpCircle className="w-4 h-4 mr-2" />
                                Upgrade to Annual
                              </>
                            ) : (
                              <>
                                <ArrowDownCircle className="w-4 h-4 mr-2" />
                                Switch to Monthly
                              </>
                            )}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleAction(sub.subscription_id, 'pause')}
                            disabled={actionLoading === sub.subscription_id}
                            className="border-orange-400/30 text-orange-400 hover:bg-orange-400/10"
                          >
                            {actionLoading === sub.subscription_id ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Pause className="w-4 h-4 mr-2" />
                            )}
                            Pause
                          </Button>
                        </>
                      )}
                      
                      {sub.status === 'paused' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleAction(sub.subscription_id, 'resume')}
                          disabled={actionLoading === sub.subscription_id}
                          className="border-green-400/30 text-green-400 hover:bg-green-400/10"
                        >
                          {actionLoading === sub.subscription_id ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Play className="w-4 h-4 mr-2" />
                          )}
                          Resume
                        </Button>
                      )}

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedSubscription(sub);
                          setShowCancelModal(true);
                        }}
                        className="border-red-400/30 text-red-400 hover:bg-red-400/10"
                      >
                        <X className="w-4 h-4 mr-2" />
                        Cancel
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Cancel Modal */}
      <Dialog open={showCancelModal} onOpenChange={setShowCancelModal}>
        <DialogContent className="bg-neutral-900 border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-400" />
              Cancel Subscription
            </DialogTitle>
            <DialogDescription className="text-neutral-400">
              Are you sure you want to cancel your subscription to {selectedSubscription?.product?.name}?
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="bg-red-400/10 border border-red-400/30 rounded-xl p-4">
              <p className="text-sm text-red-400">
                Your subscription will remain active until the end of your current billing period 
                ({formatDate(selectedSubscription?.current_period_end)}). You won&apos;t be charged again.
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-semibold mb-2">
                Reason for canceling (optional)
              </label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Help us improve by sharing why you're canceling..."
                className="w-full bg-neutral-800 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-neutral-500 min-h-[100px] focus:border-[#00F0FF] focus:outline-none"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowCancelModal(false);
                  setSelectedSubscription(null);
                  setCancelReason('');
                }}
                className="flex-1 border-white/20"
              >
                Keep Subscription
              </Button>
              <Button
                onClick={() => handleAction(selectedSubscription?.subscription_id, 'cancel', cancelReason)}
                disabled={actionLoading === selectedSubscription?.subscription_id}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white"
              >
                {actionLoading === selectedSubscription?.subscription_id ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <X className="w-4 h-4 mr-2" />
                )}
                Cancel Subscription
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upgrade/Downgrade Modal */}
      <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
        <DialogContent className="bg-neutral-900 border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <RefreshCcw className="w-5 h-5 text-[#00F0FF]" />
              Change Billing Cycle
            </DialogTitle>
            <DialogDescription className="text-neutral-400">
              {selectedSubscription?.interval === 'monthly' 
                ? 'Upgrade to annual billing and save!'
                : 'Switch to monthly billing for more flexibility.'
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {selectedSubscription?.interval === 'monthly' ? (
              <div className="bg-[#00F0FF]/10 border border-[#00F0FF]/30 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold">Annual Plan</span>
                  <span className="text-[#00F0FF] font-bold">Save ~17%</span>
                </div>
                <p className="text-sm text-neutral-400">
                  Pay once per year instead of monthly. Your subscription will be prorated.
                </p>
              </div>
            ) : (
              <div className="bg-orange-400/10 border border-orange-400/30 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold">Monthly Plan</span>
                  <span className="text-orange-400 font-bold">More Flexible</span>
                </div>
                <p className="text-sm text-neutral-400">
                  Switch to monthly billing for smaller recurring payments.
                </p>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowUpgradeModal(false);
                  setSelectedSubscription(null);
                }}
                className="flex-1 border-white/20"
              >
                Keep Current Plan
              </Button>
              <Button
                onClick={() => handleUpgrade(
                  selectedSubscription?.subscription_id, 
                  selectedSubscription?.interval === 'monthly' ? 'yearly' : 'monthly'
                )}
                disabled={actionLoading === selectedSubscription?.subscription_id}
                className="flex-1 bg-[#00F0FF] hover:bg-[#00C2CC] text-black font-bold"
              >
                {actionLoading === selectedSubscription?.subscription_id ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCcw className="w-4 h-4 mr-2" />
                )}
                {selectedSubscription?.interval === 'monthly' ? 'Upgrade to Annual' : 'Switch to Monthly'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default MySubscriptions;
